import React, { useRef, useState, useEffect, KeyboardEvent, Fragment } from 'react'
import './style.css'
import { AutoComplete, Button, Input, Tag } from 'antd'
import { SearchDescriptor } from '../../api/extra/model/search-descriptor'
import { RedoOutlined } from '@ant-design/icons'
import Icon from '../../elements/Icon'
export interface SearchTextInputProps {
  placeholder?: string
  onSearch: (descriptor?: SearchDescriptor, tags?: AtomInstance[]) => void
}

interface Props {
  type?: string
  description?: string
  selectProvider?: (value: string, ctx: any) => Promise<string[]>
}
export interface Atom {
  key: string
  system?: 'text' | 'select' | 'date'
  headAtom?: boolean
  abbrev?: string
  keyword?: string
  nextAtoms?: string[]
  nextAtomProps?: (props: Props) => Props
  isAllowed?: (atomInstances: AtomInstance[]) => boolean
}

export interface AtomInstance {
  atom: Atom
  next?: AtomInstance
  prev?: AtomInstance
  value?: string
}

const atoms: Atom[] = [
  {
    key: 'date',
    headAtom: true,
    nextAtoms: ['equal to', 'before or equal to', 'after or equal to'],
    nextAtomProps: () => ({ type: 'date' }),
    isAllowed: (atomInstances) => {
      return !(
        atomInstances.some((ai) => ai.atom.key === 'date' && ai.next?.atom?.key === 'before') &&
        atomInstances.some((ai) => ai.atom.key === 'date' && ai.next?.atom?.key === 'after')
      )
    },
  },
  {
    key: 'address',
    headAtom: true,
    nextAtoms: ['equal to'],
    nextAtomProps: () => ({ type: 'string' }),
  },
  {
    key: 'agentIdentifier',
    headAtom: true,
    nextAtoms: ['equal to'],
    nextAtomProps: () => ({ type: 'string' }),
  },
  {
    key: 'patientIdentifier',
    headAtom: true,
    nextAtoms: ['equal to'],
    nextAtomProps: () => ({ type: 'string' }),
  },
  {
    key: 'entityIdentifier',
    headAtom: true,
    nextAtoms: ['equal to'],
    nextAtomProps: () => ({ type: 'string' }),
  },
  {
    key: 'entityType',
    headAtom: true,
    nextAtoms: ['equal to'],
    nextAtomProps: () => ({ type: 'string' }),
  },
  {
    key: 'entityRole',
    headAtom: true,
    nextAtoms: ['equal to'],
    nextAtomProps: () => ({ type: 'string' }),
  },
  {
    key: 'sourceIdentifier',
    headAtom: true,
    nextAtoms: ['equal to'],
    nextAtomProps: () => ({ type: 'string' }),
  },
  {
    key: 'type',
    headAtom: true,
    nextAtoms: ['equal to'],
    nextAtomProps: () => ({ type: 'string' }),
  },
  {
    key: 'subtype',
    headAtom: true,
    nextAtoms: ['equal to'],
    nextAtomProps: () => ({ type: 'string' }),
  },
  {
    key: 'outcome',
    headAtom: true,
    nextAtoms: ['equal to'],
    nextAtomProps: () => ({ type: 'int' }),
  },
  {
    key: 'equal to',
    abbrev: '=',
    keyword: 'eq',
    nextAtoms: ['$value'],
    nextAtomProps: (props) => props,
  },
  {
    key: 'before or equal to',
    abbrev: '≤',
    keyword: 'le',
    nextAtoms: ['$value'],
    nextAtomProps: (props) => props,
  },
  {
    key: 'after or equal to',
    abbrev: '≥',
    keyword: 'ge',
    nextAtoms: ['$value'],
    nextAtomProps: (props) => props,
  },
  {
    key: 'in list',
    abbrev: 'in',
    keyword: 'in',
    nextAtoms: ['$select-multi'],
    nextAtomProps: (props) => props,
  },
  {
    key: '$value',
    system: 'text',
  },
  {
    key: '$select-multi',
    system: 'select',
    nextAtoms: ['$select-multi', 'end'],
    nextAtomProps: (props) => props,
  },
  {
    key: 'end',
  },
  {
    key: 'product',
    headAtom: true,
    nextAtoms: ['in list'],
    nextAtomProps: () => ({ selectProvider: async (text: string, ctx: any) => ctx.searchProduct(text) }),
  },
]

function tagProps(tags: AtomInstance[]) {
  return tags.reduce((props, ai) => ai.atom.nextAtomProps?.(props) || {}, {} as Props)
}

export const SearchTextInput = ({ placeholder, onSearch }: SearchTextInputProps): JSX.Element => {
  const [options, setOptions] = useState(atoms.filter((a) => a.headAtom).map((a) => ({ value: ':' + a.key })))
  const [tags, setTags] = useState<AtomInstance[]>([])
  const [text, setText] = useState('')
  const inputRef = useRef()

  const linkAndSetTags = (tags: AtomInstance[]) => {
    const lastTag = tags[tags.length - 1]
    if (lastTag && lastTag.atom.nextAtoms?.length === 1) {
      const nextAtom = atoms.find((a) => a.key === lastTag.atom.nextAtoms[0])
      if (nextAtom && !nextAtom.system) {
        linkAndSetTags(tags.concat([{ atom: nextAtom }]))
        return
      }
    }
    setTags(
      tags.map((t, i) => ({
        atom: t.atom,
        prev: i > 0 ? tags[i - 1] : undefined,
        next: i < tags.length - 1 ? tags[i + 1] : undefined,
        value: t.value,
      })),
    )
  }

  const handleSearch = async (value: string, tags: AtomInstance[]) => {
    const lastTag = tags[tags.length - 1]
    if (!lastTag || !lastTag.atom.nextAtoms) {
      setOptions(atoms.filter((a) => a.headAtom && a.key.toLocaleLowerCase().includes(value.toLocaleLowerCase())).map((a) => ({ value: ':' + a.key })))
    } else {
      const groups = lastTag.atom.nextAtoms
        .map((a: string) => atoms.find((x) => x.key === a))
        .filter((a) => a && (!a.system || a.system === 'select'))
        .reduce(
          (acc, a) => {
            if (a.system) {
              acc.selects.push(a)
            } else {
              acc.others.push(a)
            }
            return acc
          },
          { selects: [] as Atom[], others: [] as Atom[] },
        )
      if (groups.selects[0]) {
        const p = tagProps(tags)
        const selector = p.selectProvider
        setOptions(
          (selector ? await selector(value, ctx) : [])
            .map((x: string) => ({ value: x }))
            .concat(groups.others.filter((a: Atom) => a.key.toLocaleLowerCase().includes(value.toLocaleLowerCase())).map((a: Atom) => ({ value: ':' + a.key }))),
        )
      } else {
        setOptions(groups.others.filter((a: Atom) => a.key.toLocaleLowerCase().includes(value.toLocaleLowerCase())).map((a: Atom) => ({ value: ':' + a.key })))
      }
    }
  }

  useEffect(() => {
    handleSearch(text, tags).catch((e) => console.log(e))
  }, [text, tags])

  useEffect(() => {
    if (!tags[tags.length - 1]?.atom?.nextAtoms) {
      onSearch(undefined, tags)
    }
  }, [tags])

  const handleSelect = async (value: string) => {
    const lastTag = tags[tags.length - 1]
    if (!lastTag || !lastTag.atom.nextAtoms) {
      const a = atoms.find((a) => a.headAtom && ':' + a.key === value)
      a && linkAndSetTags(tags.concat([{ atom: a }]))
    } else if (value.startsWith(':')) {
      const x = lastTag.atom.nextAtoms.find((a) => ':' + a === value)
      const a = x && atoms.find((a) => a.key === x)
      a && linkAndSetTags(tags.concat([{ atom: a }]))
    } else {
      const systemNextAtoms = lastTag.atom.nextAtoms.map((a: string) => atoms.find((x) => x.key === a)).filter((a) => a && a.system)

      const select = systemNextAtoms.find((a) => a.system === 'select')
      const p = tagProps(tags)
      const selector = p.selectProvider
      const fromSelector = selector && (await selector(value, ctx))
      if (fromSelector?.includes(value)) {
        linkAndSetTags(tags.concat([{ atom: select, value: value }]))
      } else {
        const textAtom = systemNextAtoms.find((a) => a.system === 'text')
        textAtom && linkAndSetTags(tags.concat([{ atom: textAtom, value: value }]))
      }
    }
    setText('')
  }

  const ctx = {
    searchProduct: async (value: string) => ['shirt', 'suit', 'boxer'].filter((x) => x.toLocaleLowerCase().includes(value.toLocaleLowerCase())),
  }

  const handleDelete = (e: KeyboardEvent) => {
    if (e.key === 'Backspace' && !text.length && tags.length) {
      setTags(tags.filter((x, i) => i < tags.length - 1))
    }
  }

  const handleChange = (value: string) => {
    setText(value)
  }

  return (
    <Fragment>
      <div className={'search'}>
        <div className={'tagsContainer'}>
          {tags.map((t, idx) => (
            <Tag key={idx} className={'tag'} closable={idx === tags.length - 1} onClose={() => setTags(tags.filter((_, i) => i !== idx))}>
              {t.value || t.atom.abbrev || t.atom.key}
            </Tag>
          ))}
        </div>
        <AutoComplete
          options={options}
          style={{
            width: '100%',
          }}
          value={text}
          onKeyDown={handleDelete}
          onSelect={handleSelect}
          onChange={handleChange}
        >
          <Input
            ref={inputRef}
            className={'searchInput'}
            type="text"
            placeholder={placeholder}
            onBlur={() => {
              console.log('blur')
            }}
            onPressEnter={() => handleSelect(text)}
          />
        </AutoComplete>
      </div>
      <Button
        type="default"
        shape="circle"
        onClick={(x) => {
          onSearch(null, tags)
        }}
      >
        <RedoOutlined style={{ color: '#4A596E' }} />
      </Button>
    </Fragment>
  )
}
